import React, { useEffect, useRef } from 'react';
import axios from 'src/axios';

import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import { Checkbox, Chip } from '@mui/material';
import { ThemeContext, AppContext, SnackContext } from 'src/store/ContextStore';
import { CONSTANTS as c } from 'src/components/questionnaire/Constants';
import { APICONSTANTS } from 'src/components/Constants';
import { EVENTS } from 'src/components/admin/appReducer';
import { getGenotypeDisplayParts } from 'src/util/util';
import {
  deselectAllOutlines,
  selectOutline,
  getNodeFromIndex,
  distance
} from '../util';

const Container = styled(Grid)({
  display: 'flex',
  gap: 15,
  flexWrap: 'wrap'
});

const BoxHolder = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginRight: 10,

  fontSize: '1rem',
  cursor: 'grab',
  '&:active': {
    cursor: 'grabbing'
  },
  '&.dragging': {
    opacity: 0.5
  }
});

export default function GenotypesLegend({
  genotypes,
  id,
  dataset,
  setDataset,
  draggable,
  labelsSet,
  setLabelsSet,
  showCheckboxes = false
}) {
  const t = useAutoTranslation();

  const [genotypeCases, setGenotypeCases] = React.useState({});
  const [draggedItem, setDraggedItem] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const { theme } = React.useContext(ThemeContext);
  const { setSnack } = React.useContext(SnackContext);
  const { content, dispatchUpdate } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;

  const selectRadius = 50;

  const lastDragPosRef = useRef({ x: null, y: null });
  const dragImgRef = useRef(null);
  const membersRef = useRef([]);
  const prevChoiceRef = useRef({ index: -1, distBucket: null });

  React.useMemo(() => {
    const cases = {};
    (dataset || []).forEach((member) => {
      const fromGenotypes = member.genotypes || [];
      const fromDiagnoses = (member.diagnoses || []).filter((d) =>
        d.id?.startsWith('NCBIGene:')
      );
      const seen = new Set();
      [...fromGenotypes, ...fromDiagnoses].forEach((ge) => {
        if (seen.has(ge.id)) return;
        seen.add(ge.id);
        cases[ge.id] = cases[ge.id] === undefined ? 1 : cases[ge.id] + 1;
      });
    });

    setGenotypeCases(cases);
  }, [dataset, genotypes]);

  const updateData = (dataset) => {
    // setPedigreeState(CONSTANTS.pedigreeState.modified);
    setDataset(dataset);
  };

  const getNodeCenterClient = (el) => {
    if (!el) return null;

    const rect = el.getBoundingClientRect?.();
    if (rect && rect.width >= 1 && rect.height >= 1) {
      return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    }

    if (el.getBBox && el.getScreenCTM) {
      try {
        const bbox = el.getBBox();
        const ctm = el.getScreenCTM();
        const cx = bbox.x + bbox.width / 2;
        const cy = bbox.y + bbox.height / 2;
        const sx = ctm.a * cx + ctm.c * cy + ctm.e;
        const sy = ctm.b * cx + ctm.d * cy + ctm.f;
        return { x: sx, y: sy };
      } catch {
        /* ignore */
      }
    }

    const ctm = el.getScreenCTM?.();
    if (ctm) return { x: ctm.e, y: ctm.f };

    return null;
  };

  const highlightNearest = (mousePos) => {
    if (!mousePos || mousePos.x == null || mousePos.y == null) {
      return { index: -1, dist: Infinity, el: null };
    }

    if (!membersRef.current || membersRef.current.length === 0) {
      membersRef.current = Array.from(document.querySelectorAll('.node'));
    }

    let choice = { index: -1, dist: Infinity, el: null };

    for (let i = 0; i < membersRef.current.length; i++) {
      const el = membersRef.current[i];
      const center = getNodeCenterClient(el);
      if (!center) continue;
      const d = distance(mousePos, center);
      if (d < choice.dist) choice = { index: i, dist: d, el };
    }

    const threshold = selectRadius;
    const distBucket = Math.round(choice.dist / 5);
    if (
      choice.index !== prevChoiceRef.current.index ||
      distBucket !== prevChoiceRef.current.distBucket
    ) {
      prevChoiceRef.current = { index: choice.index, distBucket };
    }

    deselectAllOutlines();
    if (choice.index !== -1 && choice.dist < threshold) {
      selectOutline(choice.index, theme);
    }

    return choice;
  };

  const handleSubmit = async (index, genotype, e, nodeOverride = null) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const node = nodeOverride || getNodeFromIndex(index);

    const proBandId = node.proBandId;
    const familyMemberId = proBandId ? proBandId : node.familyMemberId;
    const userId = user?._id ?? user?.user_id ?? user?.id ?? null;
    const familyId = user?.familyId ?? user?.family?.id ?? null;

    if (!userId || !familyId)
      return setSnack({
        status: true,
        msg: 'Kindly save pedigree first!',
        severity: 'error'
      });

    const selectedItems = [genotype];

    if (selectedItems?.length === 0) {
      return setSnack({
        status: true,
        msg: 'No genotypes selected. Please add at least one genotype to proceed.',
        severity: 'error'
      });
    }

    setLoading(true);

    const updatedGenotypes = selectedItems.map((item) => ({
      id: item.id,
      name: item.name
    }));

    const payload = {
      familyId,
      familyMemberId,
      user_id: userId,
      isProband: !!proBandId,
      genotypes: updatedGenotypes
    };

    axios
      .post(APICONSTANTS.genotypes, payload)
      .then((res) => {
        if (res.data?.success) {
          dispatchUpdate({
            type: EVENTS.SET_PEDIGREE_GENOTYPES,
            value: res.data.genotypes
          });

          const new_dataset = dataset.map((elem) => {
            if (elem.pid === node.pid) {
              return {
                ...elem,
                genotypes: res.data.genotypes
              };
            }
            return elem;
          });

          updateData(new_dataset);

          setSnack({
            status: true,
            msg: 'Genotypes assigned successfully',
            severity: 'success'
          });
        }
      })
      .catch((err) =>
        setSnack({
          status: true,
          msg: err.response?.data?.error ?? 'An error has occurred!',
          severity: 'error'
        })
      )
      .finally(() => setLoading(false));

    deselectAllOutlines();
  };

  useEffect(() => {
    if (!draggedItem) return;

    const onDragOver = (e) => {
      e.preventDefault();
      const x = e.clientX ?? e.pageX ?? null;
      const y = e.clientY ?? e.pageY ?? null;
      lastDragPosRef.current = { x, y };
      highlightNearest(lastDragPosRef.current);
    };

    document.addEventListener('dragover', onDragOver, { passive: false });

    return () => {
      document.removeEventListener('dragover', onDragOver);
    };
  }, [draggedItem, theme]);

  const handleDragStart = (e, genotype) => {
    setDraggedItem(genotype);
    membersRef.current = Array.from(document.querySelectorAll('.node'));

    e.dataTransfer.setData('text/plain', genotype.name || 'genotype');
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        id: genotype.id,
        name: genotype.name,
        color: genotype.color,
        cases: genotypeCases[genotype.id] || 0,
        fullGenotype: genotype
      })
    );

    // Create custom drag chip
    const dragChip = document.createElement('div');
    dragChip.className = 'drag-chip';
    dragChip.innerHTML = `
      <div style="
        background: ${genotype.color || '#1c1c1dff'};
        color: white;
        padding: 8px 16px;
        border-radius: 20px;
        width:max-content;
      ">
        ${genotype.name}
      </div>`;

    document.body.appendChild(dragChip);
    dragImgRef.current = dragChip;
    e.dataTransfer.setDragImage(dragChip, 60, 20);

    const coords = {
      clientX: e.clientX,
      clientY: e.clientY,
      pageX: e.pageX,
      pageY: e.pageY
    };
    lastDragPosRef.current = { x: e.clientX ?? null, y: e.clientY ?? null };

    setTimeout(() => {
      if (document.body.contains(dragChip)) {
        document.body.removeChild(dragChip);
      }
    }, 0);
  };

  const handleDrag = (e) => {
    if (!draggedItem) return;
    const x = e.clientX ?? e.pageX ?? lastDragPosRef.current.x;
    const y = e.clientY ?? e.pageY ?? lastDragPosRef.current.y;
    lastDragPosRef.current = { x, y };
    highlightNearest(lastDragPosRef.current);
  };

  const handleGenotypeToggle = (id) => {
    setLabelsSet((prev) => ({
      ...prev,
      genotypesMap: {
        ...prev.genotypesMap,
        [id]: !prev.genotypesMap?.[id]
      }
    }));
  };

  const handleDragEnd = (e) => {
    if (dragImgRef.current && document.body.contains(dragImgRef.current)) {
      document.body.removeChild(dragImgRef.current);
      dragImgRef.current = null;
    }

    const raw = {
      x: e.clientX ?? e.pageX ?? null,
      y: e.clientY ?? e.pageY ?? null
    };
    const last = lastDragPosRef.current;
    let usePos = last && last.x != null && last.y != null ? last : raw;

    // Safari sometimes reports dragend far from the real last position
    if (last && raw && raw.x != null && raw.y != null) {
      const jump = Math.hypot(raw.x - last.x, raw.y - last.y);
      if (jump > 100) {
        usePos = last;
      }
    }

    const choice = highlightNearest(usePos || { x: null, y: null });
    deselectAllOutlines();

    if (choice.index !== -1 && choice.dist < selectRadius && draggedItem) {
      let nodeOverride = null;
      const el = choice.el;
      if (el) {
        const pidFromDom =
          el.dataset?.pid ||
          el.getAttribute?.('data-pid') ||
          el.getAttribute?.('pid') ||
          el.id ||
          null;

        if (pidFromDom && Array.isArray(dataset)) {
          nodeOverride = dataset.find(
            (n) => String(n.pid) === String(pidFromDom)
          );
        }
      }

      if (
        draggedItem !== 'other' &&
        !c?.pregnanciesDiagnoses?.includes(draggedItem.name)
      ) {
        handleSubmit(choice.index, draggedItem, undefined, nodeOverride);
      } else {
        handleSubmit(choice.index, draggedItem, undefined, nodeOverride);
      }
    }

    setDraggedItem(null);
    membersRef.current = [];
  };

  return (
    <>
      <Grid>
        <Container container id={id}>
          {genotypes
            .filter(
              (el) =>
                showCheckboxes ||
                !labelsSet?.genotypesMap ||
                labelsSet.genotypesMap[el.id] !== false
            )
            .map((el, i) => {
              const isDragging = draggedItem?.id === el.id;

              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center'
                  }}
                >
                  {labelsSet && showCheckboxes && (
                    <Checkbox
                      checked={labelsSet?.genotypesMap?.[el.id] ?? true}
                      onChange={() => handleGenotypeToggle(el.id)}
                      size="small"
                    />
                  )}
                  <BoxHolder
                    draggable={draggable}
                    onDragStart={(e) => handleDragStart(e, el)}
                    onDrag={handleDrag}
                    onDragEnd={handleDragEnd}
                    className={isDragging ? 'dragging' : ''}
                    title={`Drag ${el.name} to assign to a family member`}
                  >
                    <span style={{ fontSize: '1rem' }}>
                      {t(getGenotypeDisplayParts(el).symbol || el.name)}{' '}
                      <span style={{ fontSize: '0.85rem', color: '#757575' }}>
                        ({el.id})
                      </span>
                      <Chip
                        label={`${genotypeCases[el.id] ?? 0} cases`}
                        size={'small'}
                        sx={{
                          fontStyle: 'italic',
                          fontSize: 11,
                          mx: 1,
                          height: 1
                        }}
                      />
                    </span>
                  </BoxHolder>
                </div>
              );
            })}
        </Container>
      </Grid>
    </>
  );
}
